<template lang="pug">
v-autocomplete.suus__group-select(
  :attach="attach",
  v-model="selectedOption",
  :search-input.sync="search",
  :items="options",
  :label="label",
  :placeholder="placeholderText",
  persistent-placeholder,
  :item-text="itemText",
  item-value="compound_key",
  return-object,
  :loading="loading",
  :hide-no-data="hideNoData",
  :no-data-text="noDataText",
  clearable,
  filled,
  background-color="lightGrey",
  :required="required",
  :rules="rules",
  no-filter
)
  template(v-slot:label)
    span(:style="labelStyles") {{ label }}
    span(v-if="isMandatory", :style="mandatoryStyles") *
  template(v-slot:selection="{ attr, on, item, selected }")
    template(v-if="item.name")
      template(v-if="item.avatar_url")
        v-list-item-avatar(size="24", color="primary")
          img(:src="item.avatar_url")
        span {{ item.name }}
      template(v-else)
        v-list-item-avatar(size="24")
          v-icon(v-if="isGroup(item)", color="primary") {{ groupIcon }}
          v-icon(v-else, color="primary") {{ memberIcon }}
        span {{ itemLabel(item) }}
  template(v-slot:item="{ item }")
    template(v-if="item.name")
      template(v-if="item.avatar_url")
        v-list-item-avatar
          img(:src="item.avatar_url")
        v-list-item-title {{ item.name }}
      template(v-else)
        v-list-item-avatar
          v-icon(v-if="isGroup(item)", color="primary") {{ groupIcon }}
          v-icon(v-else, color="primary") {{ memberIcon }}
        v-list-item-content(v-text="itemLabel(item)")
  template(v-slot:append-item)
    p.body-2.font-weight-medium.text-center.pb-4.pt-6(v-if="specifySearch") {{ specifySearchMessage }}
  template(v-slot:no-data)
    p.body-2.font-weight-medium.text-center(v-if="loading") Bitte warten, es wird gesucht...
    p.body-2.font-weight-medium.text-center(v-else) Nichts gefunden
</template>

<script lang="ts">
import Vue, { PropType } from "vue"
import { mdiAccountCircleOutline } from "@mdi/js"
import { mdiAccountGroupOutline } from "@mdi/js"
import * as _ from "lodash"
type Mentionable = any

type GetMentionableFn = (object) => Promise<Mentionable[]>

export default Vue.extend({
  name: "GroupMemberSingleSelect",
  props: {
    isMandatory: { type: Boolean, default: true },
    label: { type: String, required: true },
    getOptions: {
      type: Function as PropType<GetMentionableFn>,
      required: true, // "Function to retrieve all options for the select. Needs to be stateless, i.e. all change-tracked variables needs to be passed as argument via getOptionsParams.",
    },
    getOptionsParams: { type: Object as PropType<object>, required: false },
    placeHolder: {
      type: String,
      default: "Name eingeben und auswählen..",
    },
    value: { type: Object as PropType<Mentionable | null>, default: null },
    noDataText: { type: String, default: "Keine Ergebnisse" },
    hideNoData: { type: Boolean, default: true },
    attach: { type: String },
    required: { type: Boolean, default: false },
    rules: {
      type: Array,
      default() {
        return []
      },
    },
    itemText: { type: String, default: "text" },
  },
  data() {
    return {
      placeholderText: this.placeHolder,
      search: "",
      memberIcon: mdiAccountCircleOutline,
      groupIcon: mdiAccountGroupOutline,
      loading: false,
      options: [this.value] as Mentionable[],
      loadOptionsDebounced: (() => Promise.resolve()) as _.DebouncedFunc<
        () => Promise<void>
      >,
    }
  },
  computed: {
    labelStyles(): object {
      return {
        fontFamily: "system-ui !important",
        color: this.$vuetify.theme.themes.light.primary,
        fontWeight: 400,
        marginBottom: 0,
      }
    },
    selectedOption: {
      get(): Mentionable {
        return this.addCompoundKey(this.value)
      },
      set(value: Mentionable) {
        this.$emit("input", this.addCompoundKey(value))
      },
    },
    mandatoryStyles(): object {
      return {
        color: "red",
        fontWeight: 400,
      }
    },
    specifySearch(): boolean {
      return this.options?.length >= 100
    },
    specifySearchMessage(): string {
      return `Deine Suche${
        this.search ? ` nach "${this.search}"` : ""
      } ergab zu viele Treffer. Bitte suche etwas genauer.`
    },
  },
  methods: {
    itemLabel(item: Mentionable): string {
      return item.name + (item.members_count ? ` (${item.members_count})` : "")
    },
    isGroup(item): boolean {
      return item.members_count !== undefined
    },
    addCompoundKey(item: Mentionable): Mentionable {
      if (!item) return item

      item.compound_key =
        (this.isGroup(item) ? "Group#" : "GroupMember#") + item.id

      return item
    },
    async loadOptions(): Promise<void> {
      this.loading = true
      try {
        const results = await this.getOptions(this.getOptionsParams || {})
        if (this.value && !results.includes(this.value)) {
          results.unshift(this.value)
        }

        // Since Group and GroupMembers may have the same ID, we introduce a compound key to distinguish them.
        this.options = results.map(this.addCompoundKey.bind(this))
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    async getOptionsParams(newVal: object, oldVal: object) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal))
        await this.loadOptions()
    },
    async search(newVal: string, oldVal: string): Promise<void> {
      if (this.loading || newVal === oldVal) return
      this.$emit("search", newVal || "")
      await this.loadOptionsDebounced()
    },
  },
  mounted: async function (): Promise<void> {
    this.options = this.options.map(this.addCompoundKey.bind(this)) // Add compound key to existing options
    this.loadOptionsDebounced = _.debounce(this.loadOptions, 500)
    await this.loadOptionsDebounced()
  },
})
</script>

<style lang="sass">
%font-stack
  font-family: system-ui !important
  font-size: 1.2rem !important
  font-weight: 300
.suus__group-select
  @extend %font-stack
  .v-label
    top: 15px
    .v-select__slot
      .v-select__selections
        @extend %font-stack
    .v-select-list
      .v-list-item__content
        @extend %font-stack
        .v-list-item
          border-bottom: 1px solid #e5e5e5 !important
</style>
